import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import { useNavigate } from 'react-router-dom';

const UpcomingSchedule = ({ orgId }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!orgId) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const today = new Date();
        const in7Days = new Date(today);
        in7Days?.setDate(today?.getDate() + 7);

        const { data } = await supabase?.from('jobs')?.select('id, title, scheduled_date, scheduled_time, status, customer_name, service_type, contacts(first_name, last_name)')?.eq('org_id', orgId)?.is('deleted_at', null)?.in('status', ['scheduled', 'in_progress'])?.gte('scheduled_date', today?.toISOString()?.split('T')?.[0])?.lte('scheduled_date', in7Days?.toISOString()?.split('T')?.[0])?.order('scheduled_date', { ascending: true })?.order('scheduled_time', { ascending: true })?.limit(6);

        setJobs(data || []);
      } catch (err) {
        console.error('Upcoming schedule error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [orgId]);

  const getClientName = (job) => {
    if (job?.customer_name) return job?.customer_name;
    if (job?.contacts) return `${job?.contacts?.first_name} ${job?.contacts?.last_name}`?.trim();
    return 'No client';
  };

  const formatScheduleDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow?.setDate(today?.getDate() + 1);

    if (d?.toDateString() === today?.toDateString()) return 'Today';
    if (d?.toDateString() === tomorrow?.toDateString()) return 'Tomorrow';
    return d?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [h, m] = timeStr?.split(':');
    const hour = parseInt(h);
    return `${hour > 12 ? hour - 12 : hour || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
  };

  const groupedByDate = jobs?.reduce((acc, job) => {
    const key = job?.scheduled_date || 'unscheduled';
    if (!acc?.[key]) acc[key] = [];
    acc?.[key]?.push(job);
    return acc;
  }, {});

  return (
    <motion.div
      className="bg-card border border-border rounded-2xl p-5 shadow-sm"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.25 }}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-foreground">Upcoming Schedule</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Next 7 days</p>
        </div>
        <button onClick={() => navigate('/calendar')} className="text-xs text-primary hover:underline font-medium">Calendar →</button>
      </div>
      {loading ? (
        <div className="space-y-3">
          {[1,2,3]?.map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded-xl" />)}
        </div>
      ) : jobs?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="CalendarCheck" size={28} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No upcoming jobs</p>
          <button onClick={() => navigate('/jobs')} className="text-xs text-primary hover:underline mt-1">Schedule a job</button>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedByDate)?.map(([date, dateJobs]) => (
            <div key={date}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-primary">{formatScheduleDate(date)}</span>
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground">{dateJobs?.length} job{dateJobs?.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="space-y-2">
                {dateJobs?.map(job => (
                  <div
                    key={job?.id}
                    className="flex items-center gap-3 p-2.5 rounded-xl bg-muted/30 hover:bg-muted/60 transition-colors cursor-pointer"
                    onClick={() => navigate('/jobs')}
                  >
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="Wrench" size={13} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">{job?.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{getClientName(job)}</p>
                    </div>
                    {job?.scheduled_time && (
                      <span className="text-xs font-medium text-muted-foreground flex-shrink-0">{formatTime(job?.scheduled_time)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default UpcomingSchedule;
